// Time zones and the Gregorian calendar as the Story room's Swift uses them (Foundation's `TimeZone`, `Calendar`,
// `Date.FormatStyle`), over `Intl`. Instants are milliseconds since 1970 (UTC), as `Date.getTime()` gives them.
//
// A zone is an IANA identifier (read through `Intl.DateTimeFormat`, which carries the tz database's history, local mean
// time before standard time included, as Foundation's ICU does) or a fixed offset from GMT (`TimeZone(secondsFromGMT:)`,
// which the Swift uses for a setting's local mean time). Wall-clock readings that a zone skips (spring forward) or
// repeats (fall back) resolve as ICU's defaults do: a skipped reading uses the offset before the change (so 02:30
// becomes 03:30), a repeated one the later occurrence.

export type Zone = { readonly id: string } | { readonly secondsFromGMT: number };

export const UTC_ZONE: Zone = { id: 'UTC' };

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(id: string): Intl.DateTimeFormat {
  let f = formatters.get(id);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: id, hourCycle: 'h23', era: 'short', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
    });
    formatters.set(id, f);
  }
  return f;
}

/** `TimeZone(identifier:)`: the zone, or null when the runtime doesn't know the identifier. */
export function zoneNamed(id: string | null | undefined): Zone | null {
  if (!id) return null;
  try {
    partsFormatter(id);
    return { id };
  } catch {
    return null;
  }
}

/** `TimeZone.current`: the runtime's zone. */
export function deviceZone(): Zone {
  return { id: new Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC' };
}

export function sameZone(a: Zone, b: Zone): boolean {
  if ('id' in a && 'id' in b) return a.id === b.id;
  if ('secondsFromGMT' in a && 'secondsFromGMT' in b) return a.secondsFromGMT === b.secondsFromGMT;
  return false;
}

export function zoneName(z: Zone): string {
  if ('id' in z) return z.id;
  const s = Math.abs(z.secondsFromGMT), sign = z.secondsFromGMT < 0 ? '-' : '+';
  const p = (n: number) => String(n).padStart(2, '0');
  return `GMT${sign}${p(Math.floor(s / 3600))}${p(Math.floor((s % 3600) / 60))}${s % 60 ? p(s % 60) : ''}`;
}

/** Milliseconds for a UTC reading, years below 100 included (which `Date.UTC` would move to the 1900s). */
export function utcMs(year: number, month: number, day: number, hour = 0, minute = 0, second = 0, ms = 0): number {
  const d = new Date(0);
  d.setUTCFullYear(year, month - 1, day);
  d.setUTCHours(hour, minute, second, ms);
  return d.getTime();
}

/** `TimeZone.secondsFromGMT(for:)` (whole seconds: the tz database's local mean times have seconds). */
export function offsetSeconds(z: Zone, ms: number): number {
  if ('secondsFromGMT' in z) return z.secondsFromGMT;
  if (z.id === 'UTC' || z.id === 'GMT') return 0;
  const whole = Math.floor(ms / 1000) * 1000;
  const parts = partsFormatter(z.id).formatToParts(new Date(whole));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const era = parts.find((p) => p.type === 'era')?.value ?? 'AD';
  let year = get('year');
  if (era.startsWith('B')) year = 1 - year;
  const wall = utcMs(year, get('month'), get('day'), get('hour') % 24, get('minute'), get('second'));
  return Math.round((wall - whole) / 1000);
}

/** A wall-clock reading in a zone (month 1–12, weekday 1 = Sunday … 7 = Saturday, as Foundation numbers them). */
export interface Components { year: number; month: number; day: number; hour: number; minute: number; second: number; ms: number; weekday: number }

export function components(ms: number, z: Zone): Components {
  const d = new Date(ms + offsetSeconds(z, ms) * 1000);
  return {
    year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(), ms: d.getUTCMilliseconds(), weekday: d.getUTCDay() + 1,
  };
}

/** `Calendar.date(from:)`: the instant a (lenient: day 31 of February is 3 March) wall-clock reading names in a zone. */
export function instant(c: Omit<Components, 'weekday'>, z: Zone): number {
  const wall = utcMs(c.year, c.month, c.day, c.hour, c.minute, c.second, c.ms);
  if ('secondsFromGMT' in z) return wall - z.secondsFromGMT * 1000;
  const before = offsetSeconds(z, wall - 86_400_000), after = offsetSeconds(z, wall + 86_400_000);
  const valid = [...new Set([before, after, offsetSeconds(z, wall)])]
    .map((o) => wall - o * 1000)
    .filter((t) => wall - offsetSeconds(z, t) * 1000 === t);
  if (valid.length) return Math.max(...valid);
  // Skipped: the offset before the change.
  return wall - before * 1000;
}

/** `TimeZone.reinterpret` of `SceneFacts`: the same wall-clock reading, in another zone. */
export function reinterpret(ms: number, from: Zone, to: Zone): number {
  if (sameZone(from, to)) return ms;
  return instant(components(ms, from), to);
}

// MARK: Calendar arithmetic (`Calendar.date(byAdding:value:to:)` and friends)

export type CalendarUnit = 'hour' | 'day' | 'week' | 'month' | 'year';

const daysIn = (year: number, month: number) => new Date(utcMs(year, month + 1, 0)).getUTCDate();

/** `date(byAdding:value:to:)`: hours as elapsed time; days and weeks on the wall clock; months and years keep the day
 *  when the month has it, else its last day. */
export function adding(unit: CalendarUnit, value: number, ms: number, z: Zone): number {
  if (unit === 'hour') return ms + value * 3_600_000;
  const c = components(ms, z);
  if (unit === 'day' || unit === 'week') return instant({ ...c, day: c.day + value * (unit === 'week' ? 7 : 1) }, z);
  const total = c.year * 12 + (c.month - 1) + value * (unit === 'year' ? 12 : 1);
  const year = Math.floor(total / 12), month = total - year * 12 + 1;
  return instant({ ...c, year, month, day: Math.min(c.day, daysIn(year, month)) }, z);
}

/** `date(bySettingHour:minute:second:of:)`. */
export function settingHour(hour: number, minute: number, second: number, ms: number, z: Zone): number {
  const c = components(ms, z);
  return instant({ ...c, hour, minute, second, ms: 0 }, z);
}

/** `nextDate(after:matching: [weekday, hour], matchingPolicy: .nextTime)`: the first such hour strictly after `ms`. */
export function nextWeekday(after: number, weekday: number, hour: number, z: Zone): number {
  const c = components(after, z);
  for (let d = 0; d <= 8; d++) {
    const t = instant({ year: c.year, month: c.month, day: c.day + d, hour, minute: 0, second: 0, ms: 0 }, z);
    if (components(t, z).weekday === weekday && t > after) return t;
  }
  return after + 7 * 86_400_000;
}

/** `dateComponents([.year], from:to:).year`: whole years between two instants, read in a zone. */
export function wholeYears(from: number, to: number, z: Zone): number {
  const a = components(from, z), b = components(to, z);
  const key = (c: Components) => [c.month, c.day, c.hour, c.minute, c.second, c.ms];
  const cmp = (x: number[], y: number[]) => { for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i]; return 0; };
  let years = b.year - a.year;
  if (to >= from) { if (years > 0 && cmp(key(b), key(a)) < 0) years -= 1; } else if (years < 0 && cmp(key(b), key(a)) > 0) years += 1;
  return years;
}

// MARK: Formatting (`Date.FormatStyle(date:time:)` with its time zone set)

export type DateStyle = 'abbreviated' | 'long' | 'omitted';
export type TimeStyle = 'shortened' | 'omitted';

const styleFormatters = new Map<string, Intl.DateTimeFormat>();

/** A date in a zone's clock, as `Date.FormatStyle` writes it in `locale` (a BCP 47 tag): abbreviated = Intl's medium,
 *  long = long, shortened time = short. English joins date and time with " at ", as Apple's formats do. */
export function formatDate(ms: number, z: Zone, date: DateStyle, time: TimeStyle, locale = 'en-US'): string {
  // Read the zone's wall clock ourselves and format it as UTC: works for fixed offsets with seconds too.
  const shifted = new Date(ms + offsetSeconds(z, ms) * 1000);
  const part = (kind: 'date' | 'time', style: string) => {
    const key = `${locale}|${kind}|${style}`;
    let f = styleFormatters.get(key);
    if (!f) {
      const options: Intl.DateTimeFormatOptions = kind === 'date'
        ? { timeZone: 'UTC', dateStyle: style as 'medium' | 'long' }
        : { timeZone: 'UTC', timeStyle: 'short' };
      try { f = new Intl.DateTimeFormat(locale, options); } catch { f = new Intl.DateTimeFormat('en-US', options); }
      styleFormatters.set(key, f);
    }
    return f.format(shifted);
  };
  const d = date === 'omitted' ? '' : part('date', date === 'long' ? 'long' : 'medium');
  const t = time === 'omitted' ? '' : part('time', 'short');
  if (d && t) return `${d}${/^en\b/i.test(locale) ? ' at ' : ', '}${t}`;
  return d || t;
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** `DateFormatter` "EEEE" in en_US_POSIX: the weekday's English name in a zone. */
export function weekdayName(ms: number, z: Zone): string {
  return WEEKDAY_NAMES[components(ms, z).weekday - 1];
}

/** `ISO8601Format()`: UTC, whole seconds. */
export function iso8601(ms: number): string {
  const d = new Date(Math.floor(ms / 1000) * 1000);
  const y = d.getUTCFullYear();
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(y, 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}Z`;
}
